import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import { LineString, Point } from 'ol/geom';
import type OlGeometry from 'ol/geom/Geometry';
import { Fill, Stroke, Style, Text } from 'ol/style';
import type { StyleFunction } from 'ol/style/Style';
import CircleStyle from 'ol/style/Circle';
import { addDirectionArrows } from './directionArrows';
import { transform } from 'ol/proj';
import type { Track, SogStop } from '@ais-ui/synthetic-geo';
import {
  type TrackColorMode,
  getSegmentColor,
  buildCategoryPalette,
  computePredictorPath,
  computeSogVector,
  getPredictionState,
  type PredictionState,
  DEFAULT_PREDICTOR_DURATION_SEC,
  DEFAULT_SOG_VECTOR_DURATION_SEC,
} from '@ais-ui/synthetic-geo';

export type { TrackColorMode };

// --- Style Options ---

export interface TrackStyleOptions {
  /** Base track color (CSS). Defaults to '#1e88e5'. Used when colorBy is 'track'. */
  color?: string;
  /** Line width in pixels. Defaults to 2.5. */
  width?: number;
  /** Line dash pattern (e.g. [8, 4] for dashed). */
  lineDash?: number[];
  /** How to color the track. Defaults to 'track' (solid color). */
  colorBy?: TrackColorMode;
  /** Custom SOG stops for dynamic classification. When provided, overrides fixed stops. */
  customSogStops?: SogStop[];
  /** Display projection (e.g. 'EPSG:3857'). Defaults to EPSG:3857. */
  displayProjection?: string;
  /** Data projection of track coordinates (e.g. 'EPSG:4326'). Defaults to EPSG:4326. */
  dataProjection?: string;
  /** Label shown on hover (e.g. vessel name). Stored as 'trackLabel' on features. */
  label?: string;
  /** Show dots at each waypoint position. */
  showPoints?: boolean;
  /** Show directional arrows along the track. */
  showArrows?: boolean;
  /** Show timestamp labels at points along the track. */
  showTimestamps?: boolean;
  /** Show the curved ROT-based predictor line from the track's last position. */
  showPredictor?: boolean;
  /** Show the straight SOG/COG vector from the track's last position. */
  showSogVector?: boolean;
  /** Predictor duration in seconds. Defaults to DEFAULT_PREDICTOR_DURATION_SEC. */
  predictorDurationSec?: number;
  /** SOG vector duration in seconds. Defaults to DEFAULT_SOG_VECTOR_DURATION_SEC. */
  sogVectorDurationSec?: number;
  /** Format an ISO 8601 timestamp into a label. Defaults to local HH:mm. */
  formatTimestamp?: (isoTimestamp: string) => string;
}

const DEFAULT_DISPLAY_PROJECTION = 'EPSG:3857';
const DEFAULT_DATA_PROJECTION = 'EPSG:4326';

// --- Display toggles ---

/**
 * The on/off display options a vector-track layer persists in its properties.
 * Centralised here so every consumer (toggle UI, color control, refresh) reads
 * and applies the same set without re-deriving each flag.
 */
export interface TrackDisplayToggles {
  showPoints: boolean;
  showArrows: boolean;
  showTimestamps: boolean;
  showPredictor: boolean;
  showSogVector: boolean;
}

/** Read the persisted display toggles off a layer's properties (all default off). */
export function getTrackDisplayToggles(
  properties?: Record<string, unknown>,
): TrackDisplayToggles {
  return {
    showPoints: properties?.showPoints === true,
    showArrows: properties?.showArrows === true,
    showTimestamps: properties?.showTimestamps === true,
    showPredictor: properties?.showPredictor === true,
    showSogVector: properties?.showSogVector === true,
  };
}

// --- Core: Pure styling function ---

/**
 * (Re)style a VectorSource with features built from a Track.
 *
 * This is the reusable core — call it to apply or change the color mode
 * on any vector track layer at any time.  Coordinates are projected from
 * the track's WGS84 path on every call (cheap for typical track sizes).
 */
export function styleTrackSource(
  source: VectorSource<Feature<OlGeometry>>,
  track: Track,
  options: TrackStyleOptions = {},
): void {
  const {
    color = '#1e88e5',
    width = 2.5,
    lineDash,
    colorBy = 'track',
    customSogStops,
    displayProjection = DEFAULT_DISPLAY_PROJECTION,
    dataProjection = DEFAULT_DATA_PROJECTION,
    label,
    showPoints = false,
    showArrows = false,
    showTimestamps = false,
    showPredictor = false,
    showSogVector = false,
    predictorDurationSec = DEFAULT_PREDICTOR_DURATION_SEC,
    sogVectorDurationSec = DEFAULT_SOG_VECTOR_DURATION_SEC,
    formatTimestamp = defaultFormatTimestamp,
  } = options;

  const coords = track.path.coordinates.map((coord) =>
    transform([coord[0], coord[1]], dataProjection, displayProjection),
  );

  source.clear();

  if (colorBy === 'track') {
    const feature = new Feature({ geometry: new LineString(coords) });
    if (label) feature.set('trackLabel', label);
    // Store waypoints + projected coords so hover can find closest point
    feature.set('trackWaypoints', track.waypoints);
    feature.set('trackCoords', coords);
    feature.setStyle(
      new Style({ stroke: new Stroke({ color, width, lineDash }) }),
    );
    source.addFeature(feature);

    // Points overlay for 'track' mode
    if (showPoints) {
      const pointStyle = createWaypointStyle(color);
      for (const coord of coords) {
        const pt = new Feature({ geometry: new Point(coord) });
        pt.setStyle(pointStyle);
        source.addFeature(pt);
      }
    }

    // Arrows overlay for 'track' mode
    if (showArrows) {
      addDirectionArrows(source, coords, color);
    }
  } else {
    const trackWithColor = { ...track, color };
    const palette = buildCategoryPalette([trackWithColor], colorBy);

    for (let i = 0; i < coords.length - 1; i++) {
      const wp = track.waypoints[i];
      if (!wp) continue;

      const segColor = getSegmentColor(
        trackWithColor,
        wp,
        colorBy,
        palette,
        customSogStops,
      );
      const segment = new Feature({
        geometry: new LineString([coords[i], coords[i + 1]]),
      });
      if (label) segment.set('trackLabel', label);
      segment.set('trackTimestamp', wp.timestamp);
      segment.set('trackSog', wp.sog_kn);
      segment.set('trackCog', wp.heading_deg ?? null);
      segment.set('trackStartCoord', coords[i]);
      segment.setStyle(
        new Style({ stroke: new Stroke({ color: segColor, width, lineDash }) }),
      );
      source.addFeature(segment);

      // Point at segment start
      if (showPoints) {
        const pt = new Feature({ geometry: new Point(coords[i]) });
        pt.setStyle(createWaypointStyle(segColor));
        source.addFeature(pt);
      }
    }

    // Last point
    if (showPoints && coords.length > 0) {
      const lastWp = track.waypoints[coords.length - 1];
      const lastColor = lastWp
        ? getSegmentColor(
            trackWithColor,
            lastWp,
            colorBy,
            palette,
            customSogStops,
          )
        : color;
      const pt = new Feature({
        geometry: new Point(coords[coords.length - 1]),
      });
      pt.setStyle(createWaypointStyle(lastColor));
      source.addFeature(pt);
    }

    // Arrows for per-segment mode
    if (showArrows) {
      addDirectionArrows(source, coords, color);
    }
  }

  // Timestamp labels (applies to both color modes)
  if (showTimestamps) {
    addTimestampLabels(source, coords, track.waypoints, color, formatTimestamp);
  }

  // Prediction overlays (dead-reckoning extrapolation from the last position).
  if (showSogVector || showPredictor) {
    const state = getPredictionState(track);
    if (state) {
      if (showSogVector) {
        addPredictionLine(
          source,
          computeSogVector(track, sogVectorDurationSec),
          dataProjection,
          displayProjection,
          {
            kind: 'sog',
            dashed: false,
            label,
            durationSec: sogVectorDurationSec,
            state,
          },
        );
      }
      if (showPredictor) {
        addPredictionLine(
          source,
          computePredictorPath(track, predictorDurationSec),
          dataProjection,
          displayProjection,
          {
            kind: 'predictor',
            dashed: true,
            label,
            durationSec: predictorDurationSec,
            state,
          },
        );
      }
    }
  }

  source.changed();
}

// --- Prediction overlays ---

const PREDICTION_COLOR = '#111827';
const PREDICTION_WIDTH = 1.5;
const PREDICTION_DASH = [6, 4];

type PredictionKind = 'sog' | 'predictor';

interface PredictionLineMeta {
  kind: PredictionKind;
  dashed: boolean;
  label?: string;
  durationSec: number;
  state: PredictionState;
}

/**
 * Add a dead-reckoning prediction line (SOG vector or curved predictor) to the
 * source. Coordinates come in as WGS84 [lon, lat] and are projected to the
 * display projection. Metadata about the motion the line is based on is stored
 * on the feature so a hover tooltip can explain it. No-op when there's nothing
 * to draw.
 */
function addPredictionLine(
  source: VectorSource<Feature<OlGeometry>>,
  lonLatCoords: number[][] | null,
  dataProjection: string,
  displayProjection: string,
  meta: PredictionLineMeta,
): void {
  if (!lonLatCoords || lonLatCoords.length < 2) return;

  const coords = lonLatCoords.map((coord) =>
    transform([coord[0], coord[1]], dataProjection, displayProjection),
  );

  const { kind, dashed, label, durationSec, state } = meta;
  const feature = new Feature({ geometry: new LineString(coords) });
  feature.set('_prediction', true);
  feature.set('predictionKind', kind);
  if (label) feature.set('predictionLabel', label);
  feature.set('predictionDurationSec', durationSec);
  feature.set('predictionCog', state.cogDeg);
  feature.set('predictionSog', state.sogKn);
  feature.set('predictionRot', state.rotDegMin);
  feature.set('predictionRotDerived', state.rotDerived);
  feature.setStyle(
    new Style({
      stroke: new Stroke({
        color: PREDICTION_COLOR,
        width: PREDICTION_WIDTH,
        lineDash: dashed ? PREDICTION_DASH : undefined,
      }),
    }),
  );
  source.addFeature(feature);
}

// --- Waypoint point style ---

function createWaypointStyle(color: string): Style {
  return new Style({
    image: new CircleStyle({
      radius: 3,
      fill: new Fill({ color }),
    }),
  });
}

// --- Timestamp labels ---

/**
 * Desired screen-pixel spacing between timestamp labels.
 * At any zoom level, labels will be ~this many pixels apart.
 */
const TIMESTAMP_SCREEN_SPACING_PX = 120;

/** Perpendicular pixel distance to push labels off the track line. */
const TIMESTAMP_LABEL_OFFSET_PX = 14;

/** Default timestamp formatter: local hours:minutes. */
function defaultFormatTimestamp(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Add timestamp text labels at points spread along the track.
 *
 * Labels are anchored to real waypoints (so the time matches an actual
 * position) and placed at roughly even distances. A resolution-aware style
 * function thins them out when zoomed out to avoid overlap.
 */
function addTimestampLabels(
  source: VectorSource<Feature<OlGeometry>>,
  coords: number[][],
  waypoints: Track['waypoints'],
  color: string,
  formatTimestamp: (isoTimestamp: string) => string,
): void {
  if (coords.length < 2 || waypoints.length === 0) return;

  const cumDist: number[] = [0];
  for (let i = 1; i < coords.length; i++) {
    const dx = coords[i][0] - coords[i - 1][0];
    const dy = coords[i][1] - coords[i - 1][1];
    cumDist.push(cumDist[i - 1] + Math.sqrt(dx * dx + dy * dy));
  }

  const totalDist = cumDist[cumDist.length - 1];
  if (totalDist === 0) return;

  const maxLabels = Math.min(40, Math.max(3, Math.floor(totalDist / 300)));
  const step = totalDist / (maxLabels + 1);

  for (let a = 1; a <= maxLabels; a++) {
    const targetDist = a * step;

    // Find the waypoint closest to this distance along the track
    let idx = coords.length - 1;
    for (let i = 1; i < cumDist.length; i++) {
      if (cumDist[i] >= targetDist) {
        idx = cumDist[i] - targetDist < targetDist - cumDist[i - 1] ? i : i - 1;
        break;
      }
    }

    const wp = waypoints[idx];
    if (!wp?.timestamp) continue;

    const text = formatTimestamp(wp.timestamp);
    if (!text) continue;

    // Offset the label perpendicular to the local track direction (in screen
    // space) so it sits beside the line instead of on top of it.
    const prev = coords[idx - 1] ?? coords[idx];
    const next = coords[idx + 1] ?? coords[idx];
    const { offsetX, offsetY } = perpendicularScreenOffset(
      prev,
      next,
      TIMESTAMP_LABEL_OFFSET_PX,
    );

    const labelFeature = new Feature({ geometry: new Point(coords[idx]) });
    labelFeature.set('_tsIndex', a);
    labelFeature.set('_tsStep', step);
    labelFeature.set('_tsText', text);
    labelFeature.set('_tsColor', color);
    labelFeature.set('_tsOffsetX', offsetX);
    labelFeature.set('_tsOffsetY', offsetY);

    labelFeature.setStyle(timestampStyleFunction);
    source.addFeature(labelFeature);
  }
}

/**
 * Compute a screen-space offset (in pixels) perpendicular to the line from
 * `from` to `to`, biased to sit above the line. Accounts for the screen Y axis
 * pointing down (opposite of map coordinates).
 */
function perpendicularScreenOffset(
  from: number[],
  to: number[],
  distance: number,
): { offsetX: number; offsetY: number } {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const len = Math.sqrt(dx * dx + dy * dy);

  // Degenerate segment — just push the label straight up.
  if (len === 0) return { offsetX: 0, offsetY: -distance };

  // Perpendicular in map space is (-dy, dx); screen Y is inverted.
  let px = -dy / len;
  let py = -dx / len;

  // Bias the label above the line (negative screen Y).
  if (py > 0) {
    px = -px;
    py = -py;
  }

  return { offsetX: px * distance, offsetY: py * distance };
}

/**
 * Resolution-aware style function for timestamp label features.
 * Thins labels when zoomed out so they stay ~TIMESTAMP_SCREEN_SPACING_PX apart.
 */
const timestampStyleFunction: StyleFunction = (featureLike, resolution) => {
  const feature = featureLike as Feature<OlGeometry>;
  const step = feature.get('_tsStep') as number;
  const index = feature.get('_tsIndex') as number;

  const mapUnitsPerLabel = TIMESTAMP_SCREEN_SPACING_PX * resolution;
  const skipFactor = Math.max(1, Math.ceil(mapUnitsPerLabel / step));

  if (index % skipFactor !== 0) return;

  const text = feature.get('_tsText') as string;
  const color = feature.get('_tsColor') as string;
  const offsetX = (feature.get('_tsOffsetX') as number) ?? 0;
  const offsetY = (feature.get('_tsOffsetY') as number) ?? -12;

  return new Style({
    image: new CircleStyle({ radius: 2.5, fill: new Fill({ color }) }),
    text: new Text({
      text,
      font: '11px sans-serif',
      textBaseline: 'middle',
      offsetX,
      offsetY,
      fill: new Fill({ color: '#fff' }),
      stroke: new Stroke({ color: 'rgba(0, 0, 0, 0.75)', width: 3 }),
    }),
  });
};

// --- Factory: create layer from track ---

/**
 * Create a new VectorLayer from a Track, pre-styled according to options.
 *
 * For initial layer creation. To restyle an existing layer later,
 * call `restyleTrackLayer` directly.
 */
export function trackPathToVectorLayer(
  track: Track,
  displayProjection: string,
  dataProjection: string,
  options: Omit<TrackStyleOptions, 'displayProjection' | 'dataProjection'> = {},
): VectorLayer<Feature<OlGeometry>> {
  const source = new VectorSource<Feature<OlGeometry>>();
  styleTrackSource(source, track, {
    ...options,
    displayProjection,
    dataProjection,
  });
  return new VectorLayer({ source });
}

// --- Restyle helper for existing layers ---

/**
 * Restyle an existing vector track layer with a new color mode or color.
 *
 * Only requires the Track (from layer properties) — coordinates are
 * derived from `track.path` every time.
 */
export function restyleTrackLayer(
  layer: VectorLayer<Feature<OlGeometry>>,
  track: Track,
  options: TrackStyleOptions = {},
): void {
  const source = layer.getSource() as VectorSource<Feature<OlGeometry>> | null;
  if (!source) return;
  styleTrackSource(source, track, options);
}
